import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { AppConfig } from '@app/app.config';
import { IResponse } from '@shared/meta-data/generic.meta';
import { HandleError, HttpErrorHandler } from '@shared/services/error-handler/http-error-handler.service';

export interface MerchantNotification {
  notification_id: number;
  notification_type?: string | null;
  title?: string | null;
  message?: string | null;
  link_path?: string | null;
  priority?: string | number | null;
  is_read: boolean;
  created_at?: string | null;
  displayTime?: string;
}

/**
 * Loads merchant-visible notifications via REST ({@link NotificationService.ensureMerchantSetupHints} on server).
 */
@Injectable({
  providedIn: 'root',
})
export class MerchantNotificationsService {
  private readonly notificationsSubject = new BehaviorSubject<MerchantNotification[]>([]);
  readonly notifications$ = this.notificationsSubject.asObservable();
  private readonly handleError: HandleError;

  constructor(
    private readonly httpClient: HttpClient,
    httpErrorHandler: HttpErrorHandler,
  ) {
    this.handleError = httpErrorHandler.createHandleError('Merchant notifications');
  }

  refresh(): Observable<MerchantNotification[]> {
    return this.httpClient.get<IResponse<unknown>>(`${AppConfig.API_URL}/notifications`).pipe(
      map(({ content }) => this.normalizeList(content)),
      map((rows) => {
        this.notificationsSubject.next(rows);
        return rows;
      }),
      catchError(this.handleError('Notifications', [])),
    );
  }

  markRead(notificationIds: string[]): Observable<MerchantNotification[]> {
    return this.httpClient
      .patch<IResponse<unknown>>(`${AppConfig.API_URL}/notifications/read`, {
        notification_ids: notificationIds,
      })
      .pipe(
        switchMap(() => this.refresh()),
        catchError(this.handleError('Mark notifications read', this.notificationsSubject.value)),
      );
  }

  /**
   * MDI class pairs for banner + navbar.
   *
   * **Must** use icon names bundled in `materialdesignicons.min.css` (Material Design Icons **v1.8.36**).
   * Newer `-outline` / hyphenated names often are missing from that file and render as empty blue boxes.
   */
  mdiIcon(notification: MerchantNotification): string[] {
    let glyph = 'mdi-information-outline';
    switch (notification.notification_type) {
      case 'SETUP_SALES_SOURCES':
        glyph = 'mdi-store';
        break;
      case 'SETUP_MERCHANT_STORE':
        glyph = 'mdi-link-variant';
        break;
      case 'SETUP_STATE_NEXUS':
        glyph = 'mdi-map-marker-radius';
        break;
      case 'SETUP_AUTOFILING':
        glyph = 'mdi-sync';
        break;
      default:
        break;
    }
    return ['mdi', glyph];
  }

  private normalizeList(raw: unknown): MerchantNotification[] {
    const list = Array.isArray(raw) ? raw : [];
    return list
      .map((row: Record<string, unknown>) => {
        const notification_id =
          typeof row.notification_id === 'number'
            ? row.notification_id
            : Number(row.notification_id);
        const created_at =
          typeof row.created_at === 'string' ? row.created_at : null;
        let displayTime = '';
        if (created_at) {
          const parsed = Date.parse(created_at);
          if (!Number.isNaN(parsed)) {
            displayTime = new Date(parsed).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            });
          }
        }
        return {
          notification_id,
          notification_type: typeof row.notification_type === 'string' ? row.notification_type : null,
          title: typeof row.title === 'string' ? row.title : null,
          message: typeof row.message === 'string' ? row.message : null,
          link_path: typeof row.link_path === 'string' ? row.link_path : null,
          priority: row.priority as string | number | null | undefined,
          is_read: Boolean(row.is_read),
          created_at,
          displayTime,
        };
      })
      .filter((n) => n.notification_type !== 'SETUP_SALES_TRANSACTIONS');
  }
}
